import React from 'react';
import ReactDOM from 'react-dom';
import bemBlock from '@gik/core/utils/bemBlock';
import { Button, Form, FormField, FormSchemaEntry } from '@gik/ui';
import { PropsWithClassName } from '@gik/core/utils/ReactUtils';
import i18n from '@gik/i18n';
import { useTranslation } from 'react-i18next';
import { translationKeys } from '@gik/inkind-page/i18n/en';

export interface IAlternatePhoneNumberFormProps {
  buttonsPortal?: () => HTMLElement;
  initialValues?: IAlternatePhoneNumberValues;
  onSubmit?: (values: IAlternatePhoneNumberValues) => void;
  onCancel?: () => void;
  closeFn?: () => void;
}

export interface IAlternatePhoneNumberValues {
  alternateNumber: string;
  alternateNumberOwnerName: string;
}

export const alternatePhoneNumberFormBlockName = 'alternate-phone-number-form';

const schema: () => FormSchemaEntry[] = () => [
  {
    name: 'alternateNumber',
    type: 'phone',
    required: true,
    minLength: 10,
    props: { maxLengthDisplay: false, placeholder: i18n.t(translationKeys.phoneNumberPlaceholder) },
  },
  {
    name: 'alternateNumberOwnerName',
    type: 'text',
    required: true,
    minLength: 2,
    maxLength: 255,
    props: { placeholder: i18n.t(translationKeys.ownerPlaceholder) },
  },
];

const AlternatePhoneNumberForm: React.FC<PropsWithClassName<IAlternatePhoneNumberFormProps>> = ({
  onSubmit,
  initialValues,
  onCancel,
  closeFn,
  buttonsPortal,
}): JSX.Element => {
  const bem = bemBlock(alternatePhoneNumberFormBlockName);
  const { t } = useTranslation();
  const formId = 'alternate-phone-number-form-id';

  const handleClick = () => {
    document.getElementById(formId).dispatchEvent(new Event('submit', { cancelable: true }));
  };

  const handleSubmit = (formData: IAlternatePhoneNumberValues) => {
    if (onSubmit) {
      onSubmit(formData);
    }
    if (closeFn) {
      closeFn();
    }
  };

  return (
    <Form
      onSubmit={handleSubmit}
      initialValues={initialValues}
      schema={schema()}
      vertical
      id={formId}
      className={bem()}
      render={({ valid, submitting }) => {
        const buttons = (
          <>
            <Button variant="default" onClick={onCancel} className={bem('button-cancel')}>
              {t(translationKeys.alternatePhoneNumberNotNow)}
            </Button>
            <Button
              className={bem('button-submit')}
              variant="primary"
              type="submit"
              loading={submitting}
              disabled={!valid}
              onClick={handleClick}
            >
              {t(translationKeys.alternatePhoneNumberSubmit)}
            </Button>
          </>
        );

        return (
          <>
            <FormField name="alternateNumber" />
            <FormField name="alternateNumberOwnerName" />
            {buttonsPortal ? ReactDOM.createPortal(buttons, buttonsPortal()) : buttons}
          </>
        );
      }}
    />
  );
};

export default AlternatePhoneNumberForm;
